// Тонкий haptic-модуль поверх Telegram WebApp HapticFeedback API.
// НЕ PlatformAdapter: одна платформа (TG), одна фича (виброотдача). Полноценная
// абстракция преждевременна.
//
// Внешний API: haptic(kind), где kind — семантическое событие геймплея, не тип
// вибрации. Маппинг семантика → API-вызов зашит внутри — если Telegram поменяет
// стили или мы захотим по-другому, правится в одном месте.
//
// Гарды: window.Telegram?.WebApp?.HapticFeedback может отсутствовать (обычный
// браузер / старый TG-клиент); каждый вызов SDK обёрнут try/catch — некоторые
// клиенты кидают на неподдерживаемых стилях (rigid/soft на старых Android'ах).

// Семантические события — согласуются с fx:*-эмитами (см. hapticBindings.ts).
export type HapticKind =
  | 'hit'
  | 'headshot'
  | 'kill'
  | 'breach'
  | 'player_hurt'
  | 'player_died'
  | 'game_over'
  | 'boss_defeated'
  | 'final_victory'
  | 'weapon_purchased'
  | 'wave_start'
  | 'wave_end'
  | 'defense_warning'
  | 'boss_wall_impact'
  | 'boss_phase_change';

type ImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type NotificationType = 'error' | 'success' | 'warning';

type HapticAction =
  | { kind: 'impact'; style: ImpactStyle }
  | { kind: 'notification'; type: NotificationType }
  | { kind: 'selection' };

// Маппинг зашит по требованию из спеки — читать таблицу тут, не в местах вызова.
const ACTION_BY_KIND: Record<HapticKind, HapticAction> = {
  hit: { kind: 'impact', style: 'light' },
  headshot: { kind: 'impact', style: 'medium' },
  kill: { kind: 'impact', style: 'medium' },
  breach: { kind: 'notification', type: 'warning' },
  player_hurt: { kind: 'impact', style: 'heavy' },
  player_died: { kind: 'notification', type: 'error' },
  game_over: { kind: 'notification', type: 'error' },
  boss_defeated: { kind: 'notification', type: 'success' },
  final_victory: { kind: 'notification', type: 'success' },
  weapon_purchased: { kind: 'notification', type: 'success' },
  wave_start: { kind: 'impact', style: 'light' },
  wave_end: { kind: 'notification', type: 'success' },
  defense_warning: { kind: 'notification', type: 'warning' },
  boss_wall_impact: { kind: 'impact', style: 'heavy' },
  boss_phase_change: { kind: 'selection' },
};

// Антиспам: hit/kill/wave_start могут лететь десятками в секунду (узи 80мс cd,
// лазер 40мс, залп 100 пуль на волну). Без троттла — сплошной зуммер + просадка
// FPS на дешёвых Android'ах. Редкие события (player_hurt/boss/victory/purchase/
// defense_warning) — без троттла, они важные и не дублируются.
const THROTTLE_MS_BY_KIND: Partial<Record<HapticKind, number>> = {
  hit: 80,
  kill: 80,
  headshot: 60,
  wave_start: 80,
};

const lastFiredAt: Partial<Record<HapticKind, number>> = {};

function getHapticFeedback(): TelegramHapticFeedback | null {
  return window.Telegram?.WebApp?.HapticFeedback ?? null;
}

export function haptic(kind: HapticKind): void {
  const throttleMs = THROTTLE_MS_BY_KIND[kind];
  if (throttleMs !== undefined) {
    const now = performance.now();
    const last = lastFiredAt[kind] ?? -Infinity;
    if (now - last < throttleMs) return;
    lastFiredAt[kind] = now;
  }

  const hf = getHapticFeedback();
  if (!hf) return;

  const action = ACTION_BY_KIND[kind];
  try {
    if (action.kind === 'impact') {
      hf.impactOccurred(action.style);
    } else if (action.kind === 'notification') {
      hf.notificationOccurred(action.type);
    } else {
      hf.selectionChanged();
    }
  } catch {
    // Часть клиентов кидает на 'rigid'/'soft' или при быстрых повторных вызовах.
    // Тактилка — не критично; глотаем, чтобы не рушить кадр.
  }
}
